<script setup>
import { computed } from "vue";

import importedProps from "./config/colProps";

const props = defineProps({ ...importedProps });

const colClasses = computed(() => {
    const cols = `col-${props.cols}`;
    const xl = `col-xl-${props.xl}`;
    const lg = `col-lg-${props.lg}`;
    const md = `col-md-${props.md}`;
    const sm = `col-sm-${props.sm}`;
    const alignSelf = `align-self-${props.alignSelf}`;
    const order = `order-${props.order}`;
    const offset = `offset-${props.offset}`;
    const offsetXl = `offset-xl-${props.offsetXl}`;
    const offsetLg = `offset-lg-${props.offsetLg}`;
    const offsetMd = `offset-md-${props.offsetMd}`;
    const offsetSm = `offset-sm-${props.offsetSm}`;

    const arrayClasses = [];

    (props.cols && arrayClasses.push(cols)) || arrayClasses.push("col");
    props.xl && arrayClasses.push(xl);
    props.lg && arrayClasses.push(lg);
    props.md && arrayClasses.push(md);
    props.sm && arrayClasses.push(sm);
    props.alignSelf && arrayClasses.push(alignSelf);
    props.order && arrayClasses.push(order);
    props.offset && arrayClasses.push(offset);
    props.offsetXl && arrayClasses.push(offsetXl);
    props.offsetLg && arrayClasses.push(offsetLg);
    props.offsetMd && arrayClasses.push(offsetMd);
    props.offsetSm && arrayClasses.push(offsetSm);

    return arrayClasses;
});
</script>

<template>
    <div :class="colClasses">
        <slot />
    </div>
</template>

<style lang="scss" scoped>
div {
    --col-percent-step-1: calc(100% * 1 / 12);
    --col-percent-step-2: calc(100% * 2 / 12);
    --col-percent-step-3: calc(100% * 3 / 12);
    --col-percent-step-4: calc(100% * 4 / 12);
    --col-percent-step-5: calc(100% * 5 / 12);
    --col-percent-step-6: calc(100% * 6 / 12);
    --col-percent-step-7: calc(100% * 7 / 12);
    --col-percent-step-8: calc(100% * 8 / 12);
    --col-percent-step-9: calc(100% * 9 / 12);
    --col-percent-step-10: calc(100% * 10 / 12);
    --col-percent-step-11: calc(100% * 11 / 12);
    --col-percent-step-12: 100%;
    --col-percent-step-auto: auto;
}

div[class^="col"] {
    position: relative;

    width: 100%;
    padding: 0 var(--row-gap-x);
}

.col {
    flex-basis: 0;
    flex-grow: 1;

    max-width: var(--col-percent-step-12);

    &-1 {
        flex: 0 0 var(--col-percent-step-1);

        max-width: var(--col-percent-step-1);
    }

    &-2 {
        flex: 0 0 var(--col-percent-step-2);

        max-width: var(--col-percent-step-2);
    }

    &-3 {
        flex: 0 0 var(--col-percent-step-3);

        max-width: var(--col-percent-step-3);
    }

    &-4 {
        flex: 0 0 var(--col-percent-step-4);

        max-width: var(--col-percent-step-4);
    }

    &-5 {
        flex: 0 0 var(--col-percent-step-5);

        max-width: var(--col-percent-step-5);
    }

    &-6 {
        flex: 0 0 var(--col-percent-step-6);

        max-width: var(--col-percent-step-6);
    }

    &-7 {
        flex: 0 0 var(--col-percent-step-7);

        max-width: var(--col-percent-step-7);
    }

    &-8 {
        flex: 0 0 var(--col-percent-step-8);

        max-width: var(--col-percent-step-8);
    }

    &-9 {
        flex: 0 0 var(--col-percent-step-9);

        max-width: var(--col-percent-step-9);
    }

    &-10 {
        flex: 0 0 var(--col-percent-step-10);

        max-width: var(--col-percent-step-10);
    }

    &-11 {
        flex: 0 0 var(--col-percent-step-11);

        max-width: var(--col-percent-step-11);
    }

    &-12 {
        flex: 0 0 var(--col-percent-step-12);

        max-width: var(--col-percent-step-12);
    }

    &-auto {
        flex: 0 0 var(--col-percent-step-auto);

        width: var(--col-percent-step-auto);
        max-width: var(--col-percent-step-12);
    }
}

.align-self {
    &-start {
        align-self: flex-start !important;
    }

    &-center {
        align-self: center !important;
    }

    &-end {
        align-self: flex-end !important;
    }

    &-baseline {
        align-self: baseline !important;
    }

    &-stretch {
        align-self: stretch !important;
    }
}

.order {
    &-1 {
        order: 1;
    }

    &-2 {
        order: 2;
    }

    &-3 {
        order: 3;
    }

    &-4 {
        order: 4;
    }

    &-5 {
        order: 5;
    }

    &-6 {
        order: 6;
    }

    &-7 {
        order: 7;
    }

    &-8 {
        order: 8;
    }

    &-9 {
        order: 9;
    }

    &-10 {
        order: 10;
    }

    &-11 {
        order: 11;
    }

    &-12 {
        order: 12;
    }
}

.offset {
    &-1 {
        margin-left: var(--col-percent-step-1);
    }

    &-2 {
        margin-left: var(--col-percent-step-2);
    }

    &-3 {
        margin-left: var(--col-percent-step-3);
    }

    &-4 {
        margin-left: var(--col-percent-step-4);
    }

    &-5 {
        margin-left: var(--col-percent-step-5);
    }

    &-6 {
        margin-left: var(--col-percent-step-6);
    }

    &-7 {
        margin-left: var(--col-percent-step-7);
    }

    &-8 {
        margin-left: var(--col-percent-step-8);
    }

    &-9 {
        margin-left: var(--col-percent-step-9);
    }

    &-10 {
        margin-left: var(--col-percent-step-10);
    }

    &-11 {
        margin-left: var(--col-percent-step-11);
    }
}

@media (min-width: 576px) {
    .col-sm {
        flex-basis: 0;
        flex-grow: 1;

        max-width: var(--col-percent-step-12);

        &-1 {
            flex: 0 0 var(--col-percent-step-1);

            max-width: var(--col-percent-step-1);
        }

        &-2 {
            flex: 0 0 var(--col-percent-step-2);

            max-width: var(--col-percent-step-2);
        }

        &-3 {
            flex: 0 0 var(--col-percent-step-3);

            max-width: var(--col-percent-step-3);
        }

        &-4 {
            flex: 0 0 var(--col-percent-step-4);

            max-width: var(--col-percent-step-4);
        }

        &-5 {
            flex: 0 0 var(--col-percent-step-5);

            max-width: var(--col-percent-step-5);
        }

        &-6 {
            flex: 0 0 var(--col-percent-step-6);

            max-width: var(--col-percent-step-6);
        }

        &-7 {
            flex: 0 0 var(--col-percent-step-7);

            max-width: var(--col-percent-step-7);
        }

        &-8 {
            flex: 0 0 var(--col-percent-step-8);

            max-width: var(--col-percent-step-8);
        }

        &-9 {
            flex: 0 0 var(--col-percent-step-9);

            max-width: var(--col-percent-step-9);
        }

        &-10 {
            flex: 0 0 var(--col-percent-step-10);

            max-width: var(--col-percent-step-10);
        }

        &-11 {
            flex: 0 0 var(--col-percent-step-11);

            max-width: var(--col-percent-step-11);
        }

        &-12 {
            flex: 0 0 var(--col-percent-step-12);

            max-width: var(--col-percent-step-12);
        }

        &-auto {
            flex: 0 0 var(--col-percent-step-auto);

            width: var(--col-percent-step-auto);
            max-width: var(--col-percent-step-12);
        }
    }

    .offset-sm {
        &-1 {
            margin-left: var(--col-percent-step-1);
        }

        &-2 {
            margin-left: var(--col-percent-step-2);
        }

        &-3 {
            margin-left: var(--col-percent-step-3);
        }

        &-4 {
            margin-left: var(--col-percent-step-4);
        }

        &-5 {
            margin-left: var(--col-percent-step-5);
        }

        &-6 {
            margin-left: var(--col-percent-step-6);
        }

        &-7 {
            margin-left: var(--col-percent-step-7);
        }

        &-8 {
            margin-left: var(--col-percent-step-8);
        }

        &-9 {
            margin-left: var(--col-percent-step-9);
        }

        &-10 {
            margin-left: var(--col-percent-step-10);
        }

        &-11 {
            margin-left: var(--col-percent-step-11);
        }
    }
}

@media (min-width: 768px) {
    .col-md {
        flex-basis: 0;
        flex-grow: 1;

        max-width: var(--col-percent-step-12);

        &-1 {
            flex: 0 0 var(--col-percent-step-1);

            max-width: var(--col-percent-step-1);
        }

        &-2 {
            flex: 0 0 var(--col-percent-step-2);

            max-width: var(--col-percent-step-2);
        }

        &-3 {
            flex: 0 0 var(--col-percent-step-3);

            max-width: var(--col-percent-step-3);
        }

        &-4 {
            flex: 0 0 var(--col-percent-step-4);

            max-width: var(--col-percent-step-4);
        }

        &-5 {
            flex: 0 0 var(--col-percent-step-5);

            max-width: var(--col-percent-step-5);
        }

        &-6 {
            flex: 0 0 var(--col-percent-step-6);

            max-width: var(--col-percent-step-6);
        }

        &-7 {
            flex: 0 0 var(--col-percent-step-7);

            max-width: var(--col-percent-step-7);
        }

        &-8 {
            flex: 0 0 var(--col-percent-step-8);

            max-width: var(--col-percent-step-8);
        }

        &-9 {
            flex: 0 0 var(--col-percent-step-9);

            max-width: var(--col-percent-step-9);
        }

        &-10 {
            flex: 0 0 var(--col-percent-step-10);

            max-width: var(--col-percent-step-10);
        }

        &-11 {
            flex: 0 0 var(--col-percent-step-11);

            max-width: var(--col-percent-step-11);
        }

        &-12 {
            flex: 0 0 var(--col-percent-step-12);

            max-width: var(--col-percent-step-12);
        }

        &-auto {
            flex: 0 0 var(--col-percent-step-auto);

            width: var(--col-percent-step-auto);
            max-width: var(--col-percent-step-12);
        }
    }

    .offset-md {
        &-1 {
            margin-left: var(--col-percent-step-1);
        }

        &-2 {
            margin-left: var(--col-percent-step-2);
        }

        &-3 {
            margin-left: var(--col-percent-step-3);
        }

        &-4 {
            margin-left: var(--col-percent-step-4);
        }

        &-5 {
            margin-left: var(--col-percent-step-5);
        }

        &-6 {
            margin-left: var(--col-percent-step-6);
        }

        &-7 {
            margin-left: var(--col-percent-step-7);
        }

        &-8 {
            margin-left: var(--col-percent-step-8);
        }

        &-9 {
            margin-left: var(--col-percent-step-9);
        }

        &-10 {
            margin-left: var(--col-percent-step-10);
        }

        &-11 {
            margin-left: var(--col-percent-step-11);
        }
    }
}

@media (min-width: 992px) {
    .col-lg {
        flex-basis: 0;
        flex-grow: 1;

        max-width: var(--col-percent-step-12);

        &-1 {
            flex: 0 0 var(--col-percent-step-1);

            max-width: var(--col-percent-step-1);
        }

        &-2 {
            flex: 0 0 var(--col-percent-step-2);

            max-width: var(--col-percent-step-2);
        }

        &-3 {
            flex: 0 0 var(--col-percent-step-3);

            max-width: var(--col-percent-step-3);
        }

        &-4 {
            flex: 0 0 var(--col-percent-step-4);

            max-width: var(--col-percent-step-4);
        }

        &-5 {
            flex: 0 0 var(--col-percent-step-5);

            max-width: var(--col-percent-step-5);
        }

        &-6 {
            flex: 0 0 var(--col-percent-step-6);

            max-width: var(--col-percent-step-6);
        }

        &-7 {
            flex: 0 0 var(--col-percent-step-7);

            max-width: var(--col-percent-step-7);
        }

        &-8 {
            flex: 0 0 var(--col-percent-step-8);

            max-width: var(--col-percent-step-8);
        }

        &-9 {
            flex: 0 0 var(--col-percent-step-9);

            max-width: var(--col-percent-step-9);
        }

        &-10 {
            flex: 0 0 var(--col-percent-step-10);

            max-width: var(--col-percent-step-10);
        }

        &-11 {
            flex: 0 0 var(--col-percent-step-11);

            max-width: var(--col-percent-step-11);
        }

        &-12 {
            flex: 0 0 var(--col-percent-step-12);

            max-width: var(--col-percent-step-12);
        }

        &-auto {
            flex: 0 0 var(--col-percent-step-auto);

            width: var(--col-percent-step-auto);
            max-width: var(--col-percent-step-12);
        }
    }

    .offset-lg {
        &-1 {
            margin-left: var(--col-percent-step-1);
        }

        &-2 {
            margin-left: var(--col-percent-step-2);
        }

        &-3 {
            margin-left: var(--col-percent-step-3);
        }

        &-4 {
            margin-left: var(--col-percent-step-4);
        }

        &-5 {
            margin-left: var(--col-percent-step-5);
        }

        &-6 {
            margin-left: var(--col-percent-step-6);
        }

        &-7 {
            margin-left: var(--col-percent-step-7);
        }

        &-8 {
            margin-left: var(--col-percent-step-8);
        }

        &-9 {
            margin-left: var(--col-percent-step-9);
        }

        &-10 {
            margin-left: var(--col-percent-step-10);
        }

        &-11 {
            margin-left: var(--col-percent-step-11);
        }
    }
}

@media (min-width: 1200px) {
    .col-xl {
        flex-basis: 0;
        flex-grow: 1;

        max-width: var(--col-percent-step-12);

        &-1 {
            flex: 0 0 var(--col-percent-step-1);

            max-width: var(--col-percent-step-1);
        }

        &-2 {
            flex: 0 0 var(--col-percent-step-2);

            max-width: var(--col-percent-step-2);
        }

        &-3 {
            flex: 0 0 var(--col-percent-step-3);

            max-width: var(--col-percent-step-3);
        }

        &-4 {
            flex: 0 0 var(--col-percent-step-4);

            max-width: var(--col-percent-step-4);
        }

        &-5 {
            flex: 0 0 var(--col-percent-step-5);

            max-width: var(--col-percent-step-5);
        }

        &-6 {
            flex: 0 0 var(--col-percent-step-6);

            max-width: var(--col-percent-step-6);
        }

        &-7 {
            flex: 0 0 var(--col-percent-step-7);

            max-width: var(--col-percent-step-7);
        }

        &-8 {
            flex: 0 0 var(--col-percent-step-8);

            max-width: var(--col-percent-step-8);
        }

        &-9 {
            flex: 0 0 var(--col-percent-step-9);

            max-width: var(--col-percent-step-9);
        }

        &-10 {
            flex: 0 0 var(--col-percent-step-10);

            max-width: var(--col-percent-step-10);
        }

        &-11 {
            flex: 0 0 var(--col-percent-step-11);

            max-width: var(--col-percent-step-11);
        }

        &-12 {
            flex: 0 0 var(--col-percent-step-12);

            max-width: var(--col-percent-step-12);
        }

        &-auto {
            flex: 0 0 var(--col-percent-step-auto);

            width: var(--col-percent-step-auto);
            max-width: var(--col-percent-step-12);
        }
    }

    .offset-xl {
        &-1 {
            margin-left: var(--col-percent-step-1);
        }

        &-2 {
            margin-left: var(--col-percent-step-2);
        }

        &-3 {
            margin-left: var(--col-percent-step-3);
        }

        &-4 {
            margin-left: var(--col-percent-step-4);
        }

        &-5 {
            margin-left: var(--col-percent-step-5);
        }

        &-6 {
            margin-left: var(--col-percent-step-6);
        }

        &-7 {
            margin-left: var(--col-percent-step-7);
        }

        &-8 {
            margin-left: var(--col-percent-step-8);
        }

        &-9 {
            margin-left: var(--col-percent-step-9);
        }

        &-10 {
            margin-left: var(--col-percent-step-10);
        }

        &-11 {
            margin-left: var(--col-percent-step-11);
        }
    }
}
</style>
